import { motion } from 'framer-motion';
import { MapPin, Trophy, Flag, Mountain, Star } from 'lucide-react';

export function RoadmapSection() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          {...fadeIn}
        >
          <h2 className="text-4xl font-bold mb-4">Our Journey</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Track our progress in developing world-class trails and nurturing champion athletes
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Bike Park Timeline */}
          <motion.div 
            className="space-y-8"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                <MapPin className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-2xl font-bold">Bike Park Evolution</h3>
            </div>

            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-1 bg-orange-200" />
              
              <div className="relative pl-20 pb-8">
                <div className="absolute left-0 w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                  2021
                </div>
                <div className="bg-orange-50 rounded-lg p-6 shadow-sm">
                  <h4 className="text-xl font-bold mb-2">Redline Trail</h4>
                  <p className="text-gray-600">First professional downhill trail inaugurated, marking our commitment to world-class facilities</p>
                </div>
              </div>

              <div className="relative pl-20 pb-8">
                <div className="absolute left-0 w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                  2022
                </div>
                <div className="bg-orange-50 rounded-lg p-6 shadow-sm">
                  <h4 className="text-xl font-bold mb-2">Blue Line</h4>
                  <p className="text-gray-600">Intermediate trail opened, expanding accessibility to more riders</p>
                </div>
              </div>

              <div className="relative pl-20 pb-8">
                <div className="absolute left-0 w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                  2024
                </div>
                <div className="bg-orange-50 rounded-lg p-6 shadow-sm">
                  <h4 className="text-xl font-bold mb-2">NH Express</h4>
                  <p className="text-gray-600">New signature trail under construction, promising an exciting riding experience</p>
                </div>
              </div>

              <div className="relative pl-20">
                <div className="absolute left-0 w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold">
                  2030
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-6 shadow-sm border-2 border-orange-100">
                  <h4 className="text-xl font-bold mb-2">Global Mountain Biking Destination</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Mountain className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <p className="text-gray-600">100km of world-class trails network</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Star className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <p className="text-gray-600">International mountain biking destination attracting riders worldwide</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Team Achievements */}
          <motion.div 
            className="space-y-8"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Trophy className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-2xl font-bold">Team Achievements</h3>
            </div>

            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-1 bg-blue-200" />

              <div className="relative pl-20 pb-8">
                <div className="absolute left-0 w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                  2020
                </div>
                <div className="bg-blue-50 rounded-lg p-6 shadow-sm">
                  <h4 className="text-xl font-bold mb-2">National Championship Success</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-blue-500" />
                      1st place Cadets - Stefan Lazar
                    </li>
                    <li className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-blue-500" />
                      2nd place Juniors - Ervin Balint
                    </li>
                  </ul>
                </div>
              </div>

              <div className="relative pl-20 pb-8">
                <div className="absolute left-0 w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                  2021
                </div>
                <div className="bg-blue-50 rounded-lg p-6 shadow-sm">
                  <h4 className="text-xl font-bold mb-2">European Cup Breakthrough</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-blue-500" />
                      3rd place Pro Masters - Marian Claudiu
                    </li>
                    <li className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-blue-500" />
                      Top 20 placements in multiple categories
                    </li>
                  </ul>
                </div>
              </div>

              <div className="relative pl-20 pb-8">
                <div className="absolute left-0 w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                  2024
                </div>
                <div className="bg-blue-50 rounded-lg p-6 shadow-sm">
                  <h4 className="text-xl font-bold mb-2">NHVelo Formation</h4>
                  <p className="text-gray-600">Strategic merger creating Romania's premier mountain biking organization</p>
                </div>
              </div>

              <div className="relative pl-20">
                <div className="absolute left-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                  2030
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 shadow-sm border-2 border-blue-100">
                  <h4 className="text-xl font-bold mb-2">World Cup Elite Performance</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Trophy className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                      <p className="text-gray-600">Multiple riders consistently placing in World Cup Top 30</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Star className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                      <p className="text-gray-600">Established as Romania's leading mountain biking development program</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
